/**
 * PortalLayout Store - Zustand store with individual selectors
 * Stores shared layout state: footer data + sidebar open/close + navigation loading state
 * Following individual selectors pattern (per zustand-patterns.md)
 */

import { create } from 'zustand';
import type { PortalLayoutResponse } from '@/application/queries/portalLayout';

// ============================================================================
// Store State
// ============================================================================

interface PortalLayoutState {
  // Data
  footerData: PortalLayoutResponse | null;
  sidebarOpen: boolean;
  loading: boolean;
  isNavigating: boolean;
  error: Error | null;

  // Actions
  setFooterData: (data: PortalLayoutResponse) => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setLoading: (loading: boolean) => void;
  setNavigating: (isNavigating: boolean) => void;
  startNavigating: () => void;
  stopNavigating: () => void;
  setError: (error: Error | null) => void;
  reset: () => void;
  _reset: () => void; // For testing
}

export interface PortalInternalNavigationOptions {
  target?: string;
  allowWhileNavigating?: boolean;
  allowSameUrl?: boolean;
}

const initialState = {
  footerData: null as PortalLayoutResponse | null,
  sidebarOpen: true,
  loading: true,
  isNavigating: false,
  error: null as Error | null,
};

const EXTERNAL_URL_REGEX = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;

const getCurrentPath = () => {
  const pathname = window.location.pathname ?? '';
  const search = window.location.search ?? '';
  const hash = window.location.hash ?? '';

  return `${pathname}${search}${hash}`;
};

const isExternalOrNewTabUrl = (url: string, target?: string) => {
  if (target === '_blank') return true;
  if (url.startsWith('//')) return true;

  return EXTERNAL_URL_REGEX.test(url);
};

// ============================================================================
// Store
// ============================================================================

const usePortalLayoutStore = create<PortalLayoutState>((set) => ({
  ...initialState,

  setFooterData: (data) =>
    set({
      footerData: data,
      loading: false,
      error: null,
    }),

  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  setLoading: (loading) => set({ loading }),

  setNavigating: (isNavigating) => set({ isNavigating }),

  startNavigating: () => set({ isNavigating: true }),

  stopNavigating: () => set({ isNavigating: false }),

  setError: (error) =>
    set({
      error,
      loading: false,
    }),

  reset: () => set({ ...initialState }),
  _reset: () => set(initialState),
}));

// ============================================================================
// Navigation Helper
// ============================================================================

export const startPortalNavigation = (): boolean => {
  const state = usePortalLayoutStore.getState();

  if (state.isNavigating) return false;

  state.startNavigating();

  return true;
};

export const navigatePortalInternal = (
  url: string,
  options?: PortalInternalNavigationOptions,
): boolean => {
  if (typeof window === 'undefined') return false;
  if (!url || !url.startsWith('/')) return false;
  if (isExternalOrNewTabUrl(url, options?.target)) return false;

  const state = usePortalLayoutStore.getState();

  if (state.isNavigating && !options?.allowWhileNavigating) {
    return false;
  }

  if (!options?.allowSameUrl && url === getCurrentPath()) {
    return false;
  }

  state.startNavigating();

  try {
    window.location.href = url;
    return true;
  } catch {
    state.stopNavigating();
    return false;
  }
};

// ============================================================================
// Individual Selector Hooks (per zustand-patterns.md)
// ============================================================================

// Data selectors
export const usePortalLayoutFooterData = () => usePortalLayoutStore((state) => state.footerData);
export const usePortalLayoutSidebarOpen = () =>
  usePortalLayoutStore((state) => state.sidebarOpen);

// State selectors
export const usePortalLayoutLoading = () => usePortalLayoutStore((state) => state.loading);
export const usePortalLayoutNavigating = () =>
  usePortalLayoutStore((state) => state.isNavigating);
export const usePortalLayoutError = () => usePortalLayoutStore((state) => state.error);

// Action selectors
export const useSetPortalLayoutFooterData = () =>
  usePortalLayoutStore((state) => state.setFooterData);
export const useSetPortalLayoutSidebarOpen = () =>
  usePortalLayoutStore((state) => state.setSidebarOpen);
export const usePortalLayoutToggleSidebar = () =>
  usePortalLayoutStore((state) => state.toggleSidebar);
export const useSetPortalLayoutLoading = () =>
  usePortalLayoutStore((state) => state.setLoading);
export const useSetPortalLayoutNavigating = () =>
  usePortalLayoutStore((state) => state.setNavigating);
export const useStartPortalLayoutNavigating = () =>
  usePortalLayoutStore((state) => state.startNavigating);
export const useStopPortalLayoutNavigating = () =>
  usePortalLayoutStore((state) => state.stopNavigating);
export const useSetPortalLayoutError = () => usePortalLayoutStore((state) => state.setError);
export const useResetPortalLayout = () => usePortalLayoutStore((state) => state.reset);

// For testing only
export const _resetPortalLayoutStore = () => usePortalLayoutStore.getState()._reset();
export const _getPortalLayoutStoreState = () => usePortalLayoutStore.getState();
